// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT

import { Injectable } from '@angular/core';
import { datadogRum } from '@datadog/browser-rum';
import { FeatureName } from './datadog-error-tracking.service';

/**
 * Action categories for better grouping
 */
export type ActionCategory =
  | 'navigation'
  | 'crud'
  | 'filter'
  | 'export'
  | 'search'
  | 'modal'
  | 'form'
  | 'interaction'
  | 'api';

/**
 * Timing measurement for performance tracking
 */
interface TimingMeasurement {
  feature: FeatureName;
  action: string;
  startTime: number;
}

/**
 * Context for feature tracking
 */
export interface FeatureContext {
  /** Feature area */
  feature: FeatureName;
  /** Sub-feature or section */
  subFeature?: string;
  /** Current page/view within feature */
  page?: string;
  /** Workspace context */
  workspaceId?: string;
  workspaceName?: string;
  /** Project context */
  projectSlug?: string;
  projectName?: string;
  /** Additional metadata */
  [key: string]: unknown;
}

/**
 * Action tracking context
 */
export interface ActionContext {
  /** Feature where action occurred */
  feature: FeatureName;
  /** Action category for grouping */
  category: ActionCategory;
  /** Specific action name */
  action: string;
  /** Component that triggered the action */
  component?: string;
  /** Result of the action */
  result?: 'success' | 'failure' | 'cancelled';
  /** Duration in milliseconds (for async actions) */
  duration?: number;
  /** Additional metadata */
  metadata?: Record<string, unknown>;
}

/**
 * Comprehensive Feature Tracking Service for Datadog RUM
 *
 * This service provides feature-level observability beyond just error tracking:
 * - Feature views and navigation
 * - User actions and interactions
 * - Performance measurements
 * - User journey tracking
 * - Feature-specific context management
 *
 * @example
 * ```typescript
 * // Set feature context when entering a feature
 * this.featureTracking.setFeatureContext({
 *   feature: 'key-projects',
 *   page: 'workspace-list',
 *   workspaceId: 'ws-123',
 * });
 *
 * // Track a user action
 * this.featureTracking.trackAction({
 *   feature: 'key-projects',
 *   category: 'crud',
 *   action: 'create-workspace',
 *   result: 'success',
 * });
 *
 * // Measure async operation duration
 * const timing = this.featureTracking.startTiming('key-projects', 'load-projects');
 * await this.loadProjects();
 * this.featureTracking.endTiming(timing);
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class DatadogFeatureTrackingService {
  private currentFeatureContext: FeatureContext | null = null;
  private activeTimings = new Map<string, TimingMeasurement>();

  /**
   * Check if Datadog RUM is initialized
   */
  private isDatadogInitialized(): boolean {
    return !!datadogRum.getInitConfiguration();
  }

  // ============================================================
  // FEATURE CONTEXT MANAGEMENT
  // ============================================================

  /**
   * Set the current feature context
   * This context will be attached to all subsequent events
   *
   * @param context - Feature context to set
   */
  setFeatureContext(context: FeatureContext): void {
    if (!this.isDatadogInitialized()) {
      return;
    }

    this.currentFeatureContext = context;

    // Set as global context in Datadog
    datadogRum.setGlobalContextProperty('feature', {
      name: context.feature,
      subFeature: context.subFeature,
      page: context.page,
      workspaceId: context.workspaceId,
      workspaceName: context.workspaceName,
      projectSlug: context.projectSlug,
      projectName: context.projectName,
      timestamp: new Date().toISOString(),
    });

    // Track feature view
    this.trackFeatureView(context);
  }

  /**
   * Update specific properties of the current feature context
   *
   * @param updates - Partial context updates
   */
  updateFeatureContext(updates: Partial<FeatureContext>): void {
    if (!this.isDatadogInitialized() || !this.currentFeatureContext) {
      return;
    }

    this.currentFeatureContext = {
      ...this.currentFeatureContext,
      ...updates,
    };

    datadogRum.setGlobalContextProperty('feature', {
      ...this.currentFeatureContext,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Clear the current feature context
   * Should be called when leaving a feature
   */
  clearFeatureContext(): void {
    if (!this.isDatadogInitialized()) {
      return;
    }

    this.currentFeatureContext = null;
    datadogRum.removeGlobalContextProperty('feature');
  }

  /**
   * Get the current feature context
   */
  getFeatureContext(): FeatureContext | null {
    return this.currentFeatureContext;
  }

  // ============================================================
  // FEATURE VIEW TRACKING
  // ============================================================

  /**
   * Track when a user views/enters a feature
   * Automatically called by setFeatureContext, but can be called manually
   *
   * @param context - Feature context
   */
  trackFeatureView(context: FeatureContext): void {
    if (!this.isDatadogInitialized()) {
      return;
    }

    datadogRum.addAction(`view:${context.feature}`, {
      type: 'feature-view',
      feature: context.feature,
      subFeature: context.subFeature,
      page: context.page,
      workspaceId: context.workspaceId,
      workspaceName: context.workspaceName,
      projectSlug: context.projectSlug,
      projectName: context.projectName,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Track a page view within a feature
   *
   * @param feature - Feature name
   * @param page - Page name within the feature
   * @param metadata - Additional metadata
   */
  trackPageView(feature: FeatureName, page: string, metadata?: Record<string, unknown>): void {
    if (!this.isDatadogInitialized()) {
      return;
    }

    datadogRum.addAction(`page:${feature}/${page}`, {
      type: 'page-view',
      feature,
      page,
      ...metadata,
      timestamp: new Date().toISOString(),
    });
  }

  // ============================================================
  // ACTION TRACKING
  // ============================================================

  /**
   * Track a user action with full context
   *
   * @param context - Action context
   */
  trackAction(context: ActionContext): void {
    if (!this.isDatadogInitialized()) {
      return;
    }

    const actionName = `${context.category}:${context.feature}/${context.action}`;

    datadogRum.addAction(actionName, {
      type: 'user-action',
      feature: context.feature,
      category: context.category,
      action: context.action,
      component: context.component,
      result: context.result,
      duration: context.duration,
      ...context.metadata,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Track a CRUD operation
   *
   * @param feature - Feature name
   * @param operation - CRUD operation (create, read, update, delete)
   * @param entity - Entity type being operated on
   * @param result - Operation result
   * @param metadata - Additional metadata
   */
  trackCrudOperation(
    feature: FeatureName,
    operation: 'create' | 'read' | 'update' | 'delete',
    entity: string,
    result: 'success' | 'failure',
    metadata?: Record<string, unknown>
  ): void {
    this.trackAction({
      feature,
      category: 'crud',
      action: `${operation}-${entity}`,
      result,
      metadata,
    });
  }

  /**
   * Track a modal interaction
   *
   * @param feature - Feature name
   * @param modalName - Name of the modal
   * @param action - Modal action (open, close, submit, cancel)
   * @param metadata - Additional metadata
   */
  trackModalInteraction(
    feature: FeatureName,
    modalName: string,
    action: 'open' | 'close' | 'submit' | 'cancel',
    metadata?: Record<string, unknown>
  ): void {
    this.trackAction({
      feature,
      category: 'modal',
      action: `${modalName}:${action}`,
      result: action === 'submit' ? 'success' : undefined,
      metadata,
    });
  }

  /**
   * Track a search operation
   *
   * @param feature - Feature name
   * @param searchType - Type of search
   * @param resultsCount - Number of results returned
   * @param metadata - Additional metadata
   */
  trackSearch(
    feature: FeatureName,
    searchType: string,
    resultsCount: number,
    metadata?: Record<string, unknown>
  ): void {
    this.trackAction({
      feature,
      category: 'search',
      action: searchType,
      result: resultsCount > 0 ? 'success' : 'failure',
      metadata: {
        resultsCount,
        ...metadata,
      },
    });
  }

  /**
   * Track an export operation
   *
   * @param feature - Feature name
   * @param exportType - Type of export (csv, pdf, etc.)
   * @param itemCount - Number of items exported
   * @param metadata - Additional metadata
   */
  trackExport(
    feature: FeatureName,
    exportType: string,
    itemCount: number,
    metadata?: Record<string, unknown>
  ): void {
    this.trackAction({
      feature,
      category: 'export',
      action: exportType,
      result: 'success',
      metadata: {
        itemCount,
        ...metadata,
      },
    });
  }

  /**
   * Track a filter change
   *
   * @param feature - Feature name
   * @param filterName - Name of the filter
   * @param filterValue - Filter value (will be sanitized)
   * @param metadata - Additional metadata
   */
  trackFilter(
    feature: FeatureName,
    filterName: string,
    filterValue: string | string[],
    metadata?: Record<string, unknown>
  ): void {
    this.trackAction({
      feature,
      category: 'filter',
      action: filterName,
      metadata: {
        filterValue: Array.isArray(filterValue) ? filterValue.join(',') : filterValue,
        ...metadata,
      },
    });
  }

  // ============================================================
  // PERFORMANCE TRACKING
  // ============================================================

  /**
   * Start timing an operation
   * Returns a timing object to pass to endTiming()
   *
   * @param feature - Feature name
   * @param action - Action being timed
   * @returns Timing measurement object
   */
  startTiming(feature: FeatureName, action: string): TimingMeasurement {
    const timing: TimingMeasurement = {
      feature,
      action,
      startTime: performance.now(),
    };

    const key = `${feature}:${action}:${timing.startTime}`;
    this.activeTimings.set(key, timing);

    return timing;
  }

  /**
   * End timing and record the measurement
   *
   * @param timing - Timing measurement from startTiming()
   * @param result - Result of the operation
   * @param metadata - Additional metadata
   */
  endTiming(
    timing: TimingMeasurement,
    result: 'success' | 'failure' = 'success',
    metadata?: Record<string, unknown>
  ): void {
    if (!this.isDatadogInitialized()) {
      return;
    }

    const duration = Math.round(performance.now() - timing.startTime);
    const key = `${timing.feature}:${timing.action}:${timing.startTime}`;
    this.activeTimings.delete(key);

    // Track as action with duration
    this.trackAction({
      feature: timing.feature,
      category: 'api',
      action: timing.action,
      result,
      duration,
      metadata,
    });

    // Also add custom timing metric
    datadogRum.addTiming(`${timing.feature}:${timing.action}`, duration);
  }

  /**
   * Measure an async operation
   * Wrapper that automatically handles timing
   *
   * @param feature - Feature name
   * @param action - Action name
   * @param operation - Async operation to measure
   * @param metadata - Additional metadata
   * @returns Result of the operation
   */
  async measureAsync<T>(
    feature: FeatureName,
    action: string,
    operation: () => Promise<T>,
    metadata?: Record<string, unknown>
  ): Promise<T> {
    const timing = this.startTiming(feature, action);

    try {
      const result = await operation();
      this.endTiming(timing, 'success', metadata);
      return result;
    } catch (error) {
      this.endTiming(timing, 'failure', {
        ...metadata,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      });
      throw error;
    }
  }

  // ============================================================
  // USER JOURNEY TRACKING
  // ============================================================

  /**
   * Track the start of a user journey/workflow
   *
   * @param feature - Feature name
   * @param journeyName - Name of the journey (e.g., 'onboarding', 'workspace-creation')
   * @param metadata - Additional metadata
   */
  startJourney(feature: FeatureName, journeyName: string, metadata?: Record<string, unknown>): void {
    if (!this.isDatadogInitialized()) {
      return;
    }

    datadogRum.addAction(`journey:${feature}/${journeyName}:start`, {
      type: 'journey-start',
      feature,
      journeyName,
      ...metadata,
      timestamp: new Date().toISOString(),
    });

    // Store journey start in session storage for duration tracking
    const key = `journey:${feature}:${journeyName}`;
    sessionStorage.setItem(key, Date.now().toString());
  }

  /**
   * Track a step within a user journey
   *
   * @param feature - Feature name
   * @param journeyName - Name of the journey
   * @param stepName - Current step name
   * @param stepNumber - Step number (1-indexed)
   * @param metadata - Additional metadata
   */
  trackJourneyStep(
    feature: FeatureName,
    journeyName: string,
    stepName: string,
    stepNumber: number,
    metadata?: Record<string, unknown>
  ): void {
    if (!this.isDatadogInitialized()) {
      return;
    }

    datadogRum.addAction(`journey:${feature}/${journeyName}:step`, {
      type: 'journey-step',
      feature,
      journeyName,
      stepName,
      stepNumber,
      ...metadata,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Track the completion of a user journey
   *
   * @param feature - Feature name
   * @param journeyName - Name of the journey
   * @param result - Journey result
   * @param metadata - Additional metadata
   */
  completeJourney(
    feature: FeatureName,
    journeyName: string,
    result: 'success' | 'failure' | 'abandoned',
    metadata?: Record<string, unknown>
  ): void {
    if (!this.isDatadogInitialized()) {
      return;
    }

    // Calculate journey duration
    const key = `journey:${feature}:${journeyName}`;
    const startTime = sessionStorage.getItem(key);
    let duration: number | undefined;

    if (startTime) {
      duration = Date.now() - parseInt(startTime, 10);
      sessionStorage.removeItem(key);
    }

    datadogRum.addAction(`journey:${feature}/${journeyName}:${result}`, {
      type: 'journey-complete',
      feature,
      journeyName,
      result,
      duration,
      ...metadata,
      timestamp: new Date().toISOString(),
    });
  }

  // ============================================================
  // BUSINESS METRICS
  // ============================================================

  /**
   * Track a business metric
   *
   * @param feature - Feature name
   * @param metricName - Name of the metric
   * @param value - Metric value
   * @param unit - Unit of measurement
   * @param metadata - Additional metadata
   */
  trackMetric(
    feature: FeatureName,
    metricName: string,
    value: number,
    unit?: string,
    metadata?: Record<string, unknown>
  ): void {
    if (!this.isDatadogInitialized()) {
      return;
    }

    datadogRum.addAction(`metric:${feature}/${metricName}`, {
      type: 'business-metric',
      feature,
      metricName,
      value,
      unit,
      ...metadata,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Track feature usage for analytics
   *
   * @param feature - Feature name
   * @param usageType - Type of usage being tracked
   * @param metadata - Additional metadata
   */
  trackFeatureUsage(
    feature: FeatureName,
    usageType: string,
    metadata?: Record<string, unknown>
  ): void {
    if (!this.isDatadogInitialized()) {
      return;
    }

    datadogRum.addAction(`usage:${feature}/${usageType}`, {
      type: 'feature-usage',
      feature,
      usageType,
      ...metadata,
      timestamp: new Date().toISOString(),
    });
  }
}

